<template>
  <div
      class="radial-metric compact"
      :style="{ width: size + 'px' }"
  >
    <div class="label">
      {{ label }}
    </div>

    <!-- Small square progress -->
    <div
        class="radial"
        :class="{ complete: percent === 100 }"
        :style="radialStyle"
        aria-hidden="true"
    >
  <span
      v-if="percent === 100"
      class="spark"
      aria-hidden="true"
  />
    </div>

    <!-- Value beneath -->
    <div class="value">
      <span v-if="type === 'percent'">
        {{ value }}%
      </span>
      <span v-else>
        {{ value }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RadialMetric',

  props: {
    value: {
      type: [Number, String],
      required: true,
    },

    label: {
      type: String,
      required: true,
    },

    size: {
      type: Number,
      default: 85,
    },

    color: {
      type: String,
      default: '#36a266',
    },

    bgColor: {
      type: String,
      default: '#eef1f4',
    },

    type: {
      type: String,
      default: 'percent',
    },
  },

  computed: {
    percent() {
      if (this.type !== 'percent') return 0;
      const p = Number(this.value);
      return Math.max(0, Math.min(100, p || 0));
    },

    radialStyle() {
      const box = Math.max(6, Math.round(this.size / 3) - 10);

      return {
        width: box + 'px',
        height: box + 'px',
        '--halo-color': this.color,
        background: `conic-gradient(
      ${this.color} ${this.percent}%,
      ${this.bgColor} ${this.percent}% 100%
    )`,
      };
    },

  },
};
</script>

<style scoped lang="scss">
.radial-metric.compact {
  display: grid;
  justify-items: center;
  align-items: center;
  grid-template-rows: 30px 40px 30px;
}

/* LABEL */
.label {
  font-size: 10px;
  font-weight: 600;
  color: #777;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

/* SMALL SQUARE PROGRESS */
.radial {
  position: relative;
  border-radius: 10px;
  filter: saturate(1.05);
}

/* Soft halo (primary colour driven) */
.radial::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 14px;
  pointer-events: none;
  box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.04),
      0 0 14px var(--halo-color);
  opacity: 0.3;
}

/* VALUE BELOW */
.value {
  font-size: 10px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

/* ===========================
   100% COMPLETION FIREWORKS
   =========================== */

.radial.complete {
  animation: complete-pulse 1.4s ease-out infinite;
}

/* Stronger halo when complete */
.radial.complete::before {
  opacity: 0.65;
  box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.04),
      0 0 22px var(--halo-color),
      0 0 42px var(--halo-color);
}

/* Spark burst */
.spark {
  position: absolute;
  inset: -10px;
  pointer-events: none;
}

.spark::before,
.spark::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  background: var(--halo-color);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: spark-burst 2s ease-out infinite;
}

.spark::after {
  animation-delay: 40s;
}

/* Animations */

@keyframes complete-pulse {
  0% {
    transform: scale(1);
    filter: saturate(1.1);
  }
  50% {
    transform: scale(1.05);
    filter: saturate(1.3);
  }
  100% {
    transform: scale(1);
    filter: saturate(1.1);
  }
}

@keyframes spark-burst {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.4);
    box-shadow:
        0 -14px 0 var(--halo-color),
        14px 0 0 var(--halo-color),
        0 14px 0 var(--halo-color),
        -14px 0 0 var(--halo-color);
  }

  30% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.2);
    box-shadow:
        0 -26px 0 transparent,
        26px 0 0 transparent,
        0 26px 0 transparent,
        -26px 0 transparent;
  }
}

</style>
